import { Slider } from './ui/slider';
import { toast } from 'sonner';
import { nursesApi } from '../lib/api/nurses';

interface NursePatientStatusUploadProps {
  onBack: () => void;
  selectedPatient?: any;
}

export function NursePatientStatusUpload({ onBack, selectedPatient }: NursePatientStatusUploadProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({
    generalCondition: '',
    mobility: '',
    breathingDifficulty: [3],
    painLevel: [0],
    adherence: '',
    mentalState: '',
    skinCondition: '',
    observations: '',
    concerns: '',
    recommendedActions: ''
  });

  const handleSubmit = async () => {
    if (!status.generalCondition || !status.mobility || !status.adherence) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!selectedPatient?.id) {
      toast.error('No patient selected');
      return;
    }

    setIsSubmitting(true);
    
    try {
      await nursesApi.submitPatientStatus({
        patient_id: selectedPatient.id,
        general_condition: status.generalCondition,
        mobility: status.mobility,
        breathing_difficulty: status.breathingDifficulty[0],
        pain_level: status.painLevel[0],
        adherence: status.adherence,
        mental_state: status.mentalState || undefined,
        skin_condition: status.skinCondition || undefined,
        observations: status.observations || undefined,
        concerns: status.concerns || undefined,
        recommended_actions: status.recommendedActions || undefined
      });

      toast.success('Patient status updated successfully!', {
        description: 'Report sent to care team'
      });

      setTimeout(() => {
        onBack();
      }, 1500);
    } catch (error: any) {
      toast.error('Failed to submit status update', {
        description: error?.message || 'Please try again'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#D4F0F8] pb-20">
      {/* Header */}
      <div className="bg-primary p-6 rounded-b-3xl shadow-md">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="text-primary-foreground">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-primary-foreground">Patient Status Update</h1>
            <p className="text-primary-foreground/80">Patient: {selectedPatient?.name} ({selectedPatient?.id})</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* General Condition */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-primary">General Assessment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Overall Condition *</Label>
              <Select value={status.generalCondition} onValueChange={(v) => setStatus({ ...status, generalCondition: v })}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select condition" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="excellent">Excellent - Very active, no complaints</SelectItem>
                  <SelectItem value="good">Good - Minor issues, generally well</SelectItem>
                  <SelectItem value="fair">Fair - Some symptoms, managing</SelectItem>
                  <SelectItem value="poor">Poor - Significant symptoms</SelectItem>
                  <SelectItem value="critical">Critical - Immediate attention needed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Mobility Status *</Label>
              <Select value={status.mobility} onValueChange={(v) => setStatus({ ...status, mobility: v })}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select mobility" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="independent">Independent - No assistance needed</SelectItem>
                  <SelectItem value="minimal-assist">Minimal Assistance - Can walk with support</SelectItem>
                  <SelectItem value="wheelchair">Wheelchair Dependent</SelectItem>
                  <SelectItem value="bedbound">Bedbound - Cannot move independently</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Mental State</Label>
              <Select value={status.mentalState} onValueChange={(v) => setStatus({ ...status, mentalState: v })}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select mental state" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="alert">Alert and Oriented</SelectItem>
                  <SelectItem value="confused">Confused or Disoriented</SelectItem>
                  <SelectItem value="lethargic">Lethargic</SelectItem>
                  <SelectItem value="anxious">Anxious or Distressed</SelectItem>
                  <SelectItem value="depressed">Showing signs of depression</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Symptoms */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-primary">Symptom Assessment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label>Breathing Difficulty</Label>
                <span className="text-primary">
                  Level {status.breathingDifficulty[0]} / 10
                </span>
              </div>
              <Slider
                value={status.breathingDifficulty}
                onValueChange={(v) => setStatus({ ...status, breathingDifficulty: v })}
                max={10}
                step={1}
                className="mt-2"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>No difficulty</span>
                <span>Severe</span>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <Label>Pain Level</Label>
                <span className={`${status.painLevel[0] > 5 ? 'text-destructive' : 'text-primary'}`}>
                  {status.painLevel[0]} / 10
                </span>
              </div>
              <Slider
                value={status.painLevel}
                onValueChange={(v) => setStatus({ ...status, painLevel: v })}
                max={10}
                step={1}
                className="mt-2"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>No pain</span>
                <span>Worst pain</span>
              </div>
              {status.painLevel[0] > 5 && (
                <div className="flex items-center gap-2 mt-2 text-destructive">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm">High pain level - Consider intervention</span>
                </div>
              )}
            </div>

            <div>
              <Label>Skin Condition</Label>
              <Select value={status.skinCondition} onValueChange={(v) => setStatus({ ...status, skinCondition: v })}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select condition" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal - No issues</SelectItem>
                  <SelectItem value="dry">Dry skin</SelectItem>
                  <SelectItem value="redness">Redness or irritation</SelectItem>
                  <SelectItem value="bruising">Bruising present</SelectItem>
                  <SelectItem value="wounds">Wounds or pressure sores</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Adherence */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-primary">Treatment Adherence *</CardTitle>
          </CardHeader>
          <CardContent>
            <RadioGroup value={status.adherence} onValueChange={(v) => setStatus({ ...status, adherence: v })}>
              <div className="flex items-center space-x-2 p-3 bg-[#14CAF4]/10 rounded-lg mb-2">
                <RadioGroupItem value="excellent" id="excellent" />
                <label htmlFor="excellent" className="flex items-center gap-2 flex-1 cursor-pointer">
                  <ThumbsUp className="w-4 h-4 text-[#14CAF4]" />
                  <div>
                    <p className="text-foreground">Excellent</p>
                    <p className="text-muted-foreground text-sm">Following all instructions</p>
                  </div>
                </label>
              </div>

              <div className="flex items-center space-x-2 p-3 bg-secondary rounded-lg mb-2">
                <RadioGroupItem value="good" id="good" />
                <label htmlFor="good" className="flex-1 cursor-pointer">
                  <p className="text-foreground">Good</p>
                  <p className="text-muted-foreground text-sm">Mostly following instructions</p>
                </label>
              </div>

              <div className="flex items-center space-x-2 p-3 bg-secondary rounded-lg mb-2">
                <RadioGroupItem value="fair" id="fair" />
                <label htmlFor="fair" className="flex-1 cursor-pointer">
                  <p className="text-foreground">Fair</p>
                  <p className="text-muted-foreground text-sm">Sometimes non-compliant</p>
                </label>
              </div>

              <div className="flex items-center space-x-2 p-3 bg-destructive/10 rounded-lg">
                <RadioGroupItem value="poor" id="poor" />
                <label htmlFor="poor" className="flex items-center gap-2 flex-1 cursor-pointer">
                  <ThumbsDown className="w-4 h-4 text-destructive" />
                  <div>
                    <p className="text-foreground">Poor</p>
                    <p className="text-muted-foreground text-sm">Not following treatment plan</p>
                  </div>
                </label>
              </div>
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Clinical Notes */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-primary">Clinical Observations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Observations & Notes</Label>
              <Textarea
                value={status.observations}
                onChange={(e) => setStatus({ ...status, observations: e.target.value })}
                placeholder="Document patient's appearance, behavior, environment, any notable changes..."
                className="mt-2"
                rows={4}
              />
            </div>

            <div>
              <Label>Concerns or Red Flags</Label>
              <Textarea
                value={status.concerns}
                onChange={(e) => setStatus({ ...status, concerns: e.target.value })}
                placeholder="Any concerns that require immediate attention from the care team..."
                className="mt-2"
                rows={3}
              />
            </div>

            <div>
              <Label>Recommended Actions</Label>
              <Textarea
                value={status.recommendedActions}
                onChange={(e) => setStatus({ ...status, recommendedActions: e.target.value })}
                placeholder="Suggested follow-up actions, medication adjustments, etc..."
                className="mt-2"
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Alert if Critical */}
        {(status.generalCondition === 'critical' || status.painLevel[0] > 7 || status.breathingDifficulty[0] > 7) && (
          <Card className="shadow-lg bg-destructive/10 border-destructive">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-destructive mb-1">Critical Status Alert</h4>
                  <p className="text-foreground text-sm">
                    Patient condition requires immediate medical attention. Care coordinator and doctor will be notified automatically.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onBack}>
            Cancel
          </Button>
          <Button className="flex-1 bg-accent hover:bg-accent/90" onClick={handleSubmit} disabled={isSubmitting}>
            <Save className="w-4 h-4 mr-2" />
            Submit Status Update
          </Button>
        </div>
      </div>
    </div>
  );
}